import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@figma/fpl-components';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import AppDivider from '@/UI/AppDivider/AppDivider';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { AppToggleButton } from '@/UI/AppToggleButtons';
import { Box } from '@/UI/Box/Box';
import { Chip } from '@/UI/Chip/Chip';
import { DragHandleIcon, ThreeDotsVerticalIcon } from '@/UI/Icons';
import { LineInput } from '@/UI/Input/Input';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { FlexCenVer, FlexCol, FlexCenVerSpaceBetween } from '@/UI/styles';
import { DesignAppMode } from '@/enums/design-app-modes.enum';
import { I18N_KEYS } from '@/language/keys';
import { BaseNodeData } from '@/types/node';
import type { NodeId } from 'web';

type Node = {
  id: NodeId;
  data: {
    name: string;
    description: string;
  };
};

type MenuAction = { label: string; onClick: () => void; disabled?: boolean; id: string };

/** An input's name with the required marker, which takes the label's own color. */
export function requiredLabel(name: string, required?: boolean): string {
  return required ? `${name} *` : name;
}

export type DescriptionRenderProps = {
  description: string;
  plainLabel?: boolean;
  inEditMode: boolean;
  editingDescription: boolean;
  setEditingDescription: (editing: boolean) => void;
  onDescriptionChange: (description: string) => void;
};

export type DesignAppInputHeaderProps = {
  inputId: NodeId;
  updateNodeData: (id: NodeId, data: Partial<BaseNodeData>) => void;
  role: string;
  inputColor: string;
  nodes: Node[];
  displayDescription?: string;
  setIsEditingInputMetadata: (isEditing: boolean) => void;
  mode: DesignAppMode;
  actions?: MenuAction[];
  renderDescription?: (props: DescriptionRenderProps) => React.ReactNode;
  plainLabel?: boolean;
  /** Marks the name as required. Not shown while editing, where the name is the rename field. */
  required?: boolean;
  /** Supplied by {@link DesignApp} via {@link useFlowView}, or a mock in Storybook. */
  renameNodeDisplayName: (nodeId: NodeId, newName: string) => void;
};

function DesignAppInputHeader({
  inputId,
  updateNodeData,
  role,
  inputColor,
  nodes,
  displayDescription,
  setIsEditingInputMetadata,
  mode,
  actions,
  renderDescription,
  plainLabel = false,
  required = false,
  renameNodeDisplayName,
}: DesignAppInputHeaderProps) {
  const { t } = useTranslation();
  const [editingInputId, setEditingInputId] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState(false);
  const anchorEl = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const inEditMode = role === 'editor' && mode === DesignAppMode.Editing;

  useEffect(() => {
    if (editingInputId || editingDescription) {
      setIsEditingInputMetadata(true);
    } else {
      setIsEditingInputMetadata(false);
    }
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, [editingInputId, editingDescription]);

  const handleInputNameChange = useCallback(
    (newName: string) => {
      if (!inEditMode) {
        return;
      }
      const trimmed = newName.trim();
      if (!trimmed) {
        return;
      }
      renameNodeDisplayName(inputId, trimmed);
    },
    [inputId, inEditMode, renameNodeDisplayName],
  );

  const handleInputDescriptionChange = (newDescription: string) => {
    if (inEditMode) {
      updateNodeData(inputId, { description: newDescription });
    }
  };

  const currentNode = nodes.find((node) => node.id === inputId);

  if (!currentNode) {
    return null;
  }

  const descriptionProps: DescriptionRenderProps = {
    description: editingDescription
      ? currentNode.data.description
      : (displayDescription ?? currentNode.data.description),
    plainLabel,
    inEditMode,
    editingDescription,
    setEditingDescription,
    onDescriptionChange: handleInputDescriptionChange,
  };

  if (plainLabel && !inEditMode) {
    return (
      <FlexCol sx={{ width: '100%', mb: 1, gap: 1 }}>
        <Text.div variant="body-medium" color="default">
          {requiredLabel(currentNode.data.name, required)}
        </Text.div>
        {descriptionProps.description &&
          (renderDescription ? renderDescription(descriptionProps) : <DefaultDescription {...descriptionProps} />)}
      </FlexCol>
    );
  }

  return (
    <FlexCol
      sx={{
        width: '100%',
        mb: 1,
      }}
    >
      <FlexCenVerSpaceBetween
        sx={{
          width: '100%',
        }}
      >
        <FlexCenVer sx={{ ml: -0.8, flexGrow: 1, minWidth: 0 }}>
          <FlexCenVer
            sx={{
              gap: 1,
              width: editingInputId === inputId ? 'clamp(90px, calc(80px + 5%), 150px)' : undefined,
            }}
          >
            {inEditMode && (
              <FlexCenVer sx={{ width: '16px' }}>
                <DragHandleIcon style={{ opacity: 0.8, cursor: 'grab' }} aria-hidden />
              </FlexCenVer>
            )}
            {editingInputId === inputId ? (
              <Box sx={{ fontSize: '0.8125rem', flex: 1 }}>
                <LineInput
                  autoFocus
                  fullWidth
                  size="small"
                  defaultValue={currentNode.data.name}
                  onFocus={(e) => {
                    e.target.select();
                  }}
                  onBlur={(e) => {
                    handleInputNameChange(e.target.value);
                    setEditingInputId(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && 'value' in e.target && typeof e.target.value === 'string') {
                      handleInputNameChange(e.target.value);
                      setEditingInputId(null);
                    }
                    if (e.key === 'Escape') {
                      setEditingInputId(null);
                    }
                  }}
                  sx={{
                    fontSize: '0.8125rem',
                    '&:before, &:after': {
                      borderBottom: `1px solid ${inputColor}`,
                    },
                  }}
                />
              </Box>
            ) : (
              <Chip
                aria-description={t(I18N_KEYS.GENERAL.RENAME)}
                label={requiredLabel(currentNode.data.name, required && !inEditMode)}
                size="small"
                sx={{
                  background: inputColor,
                  cursor: inEditMode ? 'text' : 'default',
                  '&:hover': {
                    background: inEditMode ? inputColor : 'transparent',
                  },
                }}
                onClick={() => inEditMode && setEditingInputId(inputId)}
              />
            )}
          </FlexCenVer>
          <Box sx={{ ml: 1, flex: 1 }}>
            <AppDivider />
          </Box>
        </FlexCenVer>
        {inEditMode ? (
          <Box>
            <AppToggleButton
              ref={anchorEl}
              value="input-actions"
              selected={open}
              btnW={24}
              btnH={24}
              isIcon
              aria-label="Open input settings"
              aria-haspopup="menu"
              onClick={() => setOpen(true)}
              sx={{ border: 'none' }}
            >
              <ThreeDotsVerticalIcon />
            </AppToggleButton>
            <AppContextMenu
              anchorEl={anchorEl.current}
              open={open}
              onClose={() => setOpen(false)}
              width="140px"
              offset={{ top: '4px' }}
              items={actions?.map((action) => ({
                biEventKey: { menu: MENU_NAMES.DESIGN_APP_INPUT, action: 'action' },
                name: action.label,
                disabled: action.disabled,
                action: action.onClick,
              }))}
            />
          </Box>
        ) : null}
      </FlexCenVerSpaceBetween>
      <Box id="design-appinput-description" sx={{ mt: 1 }}>
        {renderDescription ? renderDescription(descriptionProps) : <DefaultDescription {...descriptionProps} />}
      </Box>
    </FlexCol>
  );
}

function DescriptionBase({
  displayText,
  plainLabel,
  editDefaultValue,
  skipEmptySave,
  inEditMode,
  editingDescription,
  setEditingDescription,
  onDescriptionChange,
}: {
  displayText: string;
  plainLabel?: boolean;
  editDefaultValue: string;
  skipEmptySave?: boolean;
  inEditMode: boolean;
  editingDescription: boolean;
  setEditingDescription: (editing: boolean) => void;
  onDescriptionChange: (description: string) => void;
}) {
  const { t: translate } = useTranslation();

  if (editingDescription) {
    return (
      <LineInput
        autoFocus
        fullWidth
        multiline
        size="small"
        minRows={1}
        maxRows={12}
        placeholder={translate(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.HEADER.DESCRIPTION_PLACEHOLDER)}
        aria-label={translate(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.HEADER.DESCRIPTION_PLACEHOLDER)}
        defaultValue={editDefaultValue}
        onBlur={(e) => {
          if (!skipEmptySave || e.target.value) {
            onDescriptionChange(e.target.value);
          }
          setEditingDescription(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && 'value' in e.target && typeof e.target.value === 'string') {
            onDescriptionChange(e.target.value);
            setEditingDescription(false);
          }
          if (e.key === 'Escape') {
            setEditingDescription(false);
          }
        }}
        inputProps={{ style: { resize: 'none' } }}
        sx={{ mt: 0 }}
      />
    );
  }

  if (plainLabel && !inEditMode) {
    return (
      <Text.div variant="body-medium" color="secondary" truncate hideTooltip>
        {displayText}
      </Text.div>
    );
  }

  return (
    <TextSmRg
      noWrap
      component="div"
      sx={{ cursor: inEditMode ? 'text' : 'default' }}
      onClick={() => inEditMode && setEditingDescription(true)}
    >
      {displayText}
    </TextSmRg>
  );
}

function DefaultDescription({ description, inEditMode, ...rest }: DescriptionRenderProps) {
  const { t: translate } = useTranslation();
  const displayText =
    description || (inEditMode ? translate(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.HEADER.ADD_DESCRIPTION) : '');

  return <DescriptionBase displayText={displayText} editDefaultValue={description} inEditMode={inEditMode} {...rest} />;
}

export function IteratorDescription({
  description,
  placeholderDescription,
  inEditMode,
  ...rest
}: DescriptionRenderProps & { placeholderDescription: string }) {
  const { t: translate } = useTranslation();
  const hasCustomDescription = !!description && description !== placeholderDescription;
  const displayText = hasCustomDescription
    ? description
    : inEditMode
      ? translate(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.HEADER.ADD_DESCRIPTION)
      : '';

  return (
    <DescriptionBase
      displayText={displayText}
      editDefaultValue={hasCustomDescription ? description : ''}
      skipEmptySave
      inEditMode={inEditMode}
      {...rest}
    />
  );
}

export default DesignAppInputHeader;
